import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icons } from 'components/Icons';
import CodeBlock from 'components/CodeBlock';
import { useDarkMode } from 'utils/darkMode';
import { useTranslation } from 'utils/i18n';
import DownloadDocumentationModal from './DownloadDocumentationModal';
import { generateDocumentationPDF, generateDocumentationTXT } from 'utils/documentationDownload';
import './DocumentationLayout.css';

/**
 * DocumentationLayout Component
 * Professional three-column documentation layout:
 * - Left sidebar: Navigation menu with sections
 * - Main content: Documentation content with code blocks
 * - Right sidebar: "On this page" table of contents
 * 
 * Based on the layout from image.png reference
 */
const DocumentationLayout = ({
  // Navigation structure
  sections = [], // Array of { id, title, icon?, items: [{ id, title, description, tags?, content }] }
  
  // Content rendering
  renderContent, // Function to render custom content: (item) => ReactNode
  renderCodeBlock, // Function to render custom code blocks: (code, language) => ReactNode
  
  // Search
  searchEnabled = true,
  searchPlaceholder = 'Search documentation...',
  
  // Callbacks
  onSectionChange, // (sectionId, itemId) => void
  onItemSelect, // (itemId) => void
  
  // Styling
  className = '',
  
  // Mobile behavior
  mobileSidebarOpen = false,
  onMobileSidebarToggle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useDarkMode();
  useTranslation();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSection, setSelectedSection] = useState(sections[0]?.id || '');
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  
  const mainContentRef = useRef(null);
  const leftSidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Get current section and item from URL
  useEffect(() => {
    if (sections.length === 0) return;
    
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length >= 3 && pathParts[0] === 'docs') {
      const sectionFromURL = pathParts[1];
      const itemFromURL = pathParts[2];
      
      const section = sections.find(s => s.id === sectionFromURL);
      if (section) {
        const item = section.items?.find(i => i.id === itemFromURL);
        if (item) {
          setSelectedSection(sectionFromURL);
          setSelectedItem(itemFromURL);
          if (onSectionChange) onSectionChange(sectionFromURL, itemFromURL);
          return;
        }
      }
    }
    
    // Default to first section/item if no valid URL match
    if (!selectedItem && sections.length > 0) {
      const firstSection = sections[0];
      const firstItem = firstSection.items?.[0];
      if (firstItem) {
        setSelectedSection(firstSection.id);
        setSelectedItem(firstItem.id);
        navigate(`/docs/${firstSection.id}/${firstItem.id}`, { replace: true });
      }
    }
  }, [location.pathname, sections, navigate, onSectionChange]);
  
  // Scroll spy for "On this page" TOC
  useEffect(() => {
    const container = mainContentRef.current;
    if (!container) return;
    
    const headings = container.querySelectorAll('h1, h2, h3, h4, [data-heading-id]');
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        
        if (visible.length > 0) {
          const id = visible[0].target.getAttribute('data-heading-id') || 
                     visible[0].target.id ||
                     visible[0].target.textContent.toLowerCase().replace(/\s+/g, '-');
          setActiveHeadingId(id);
        }
      },
      { root: null, rootMargin: '-20% 0px -70% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    
    headings.forEach(h => observer.observe(h));
    return () => observer.disconnect();
  }, [selectedItem]);
  
  // Filter sections/items based on search
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;
    
    const query = searchQuery.toLowerCase();
    return sections
      .map(section => ({
        ...section,
        items: section.items?.filter(item => 
          item.title.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.tags?.some(tag => tag.toLowerCase().includes(query))
        ) || []
      }))
      .filter(section => section.items.length > 0);
  }, [sections, searchQuery]);
  
  // Get current item (skip group headers)
  const currentItem = useMemo(() => {
    const section = sections.find(s => s.id === selectedSection);
    const item = section?.items?.find(i => i.id === selectedItem);
    // Don't allow group headers to be selected
    if (item?.groupHeader) {
      return null;
    }
    return item;
  }, [sections, selectedSection, selectedItem]);
  
  // Auto-expand groups containing selected item
  useEffect(() => {
    if (!selectedSection || !selectedItem) return;
    
    const section = sections.find(s => s.id === selectedSection);
    if (!section?.items) return;
    
    // Find which group contains the selected item
    let currentGroupId = null;
    for (const item of section.items) {
      if (item.groupHeader) {
        currentGroupId = item.id;
      } else if (item.id === selectedItem && currentGroupId) {
        // Auto-expand the group containing the selected item
        setExpandedGroups(prev => ({
          ...prev,
          [`${selectedSection}-${currentGroupId}`]: true
        }));
        break;
      }
    }
  }, [selectedSection, selectedItem, sections]);
  
  // Extract headings from current item content for TOC
  const headings = useMemo(() => {
    if (!currentItem?.content) return [];
    
    const headingList = [];
    
    // Extract from steps
    if (currentItem.content.steps) {
      currentItem.content.steps.forEach((step, index) => {
        if (step.title) {
          headingList.push({
            id: `step-${index}`,
            title: step.title,
            level: 2
          });
        }
      });
    }
    
    // Extract from methods
    if (currentItem.content.methods) {
      currentItem.content.methods.forEach((method, index) => {
        if (method.title) {
          headingList.push({
            id: `method-${index}`,
            title: method.title,
            level: 2
          });
        }
      });
    }
    
    // Extract from setup
    if (currentItem.content.setup) {
      currentItem.content.setup.forEach((item, index) => {
        if (item.title) {
          headingList.push({
            id: `setup-${index}`,
            title: item.title,
            level: 2
          });
        }
      });
    }
    
    return headingList;
  }, [currentItem]);
  
  // Handle section toggle
  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };
  
  // Handle group toggle
  const handleGroupToggle = (groupId, sectionId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [`${sectionId}-${groupId}`]: !prev[`${sectionId}-${groupId}`]
    }));
  };
  
  // Handle item selection
  const handleItemSelect = (sectionId, itemId) => {
    setSelectedSection(sectionId);
    setSelectedItem(itemId);
    navigate(`/docs/${sectionId}/${itemId}`, { replace: true });
    
    if (onItemSelect) onItemSelect(itemId);
    if (onSectionChange) onSectionChange(sectionId, itemId);
    
    // Close mobile sidebar
    if (isMobile && onMobileSidebarToggle) {
      onMobileSidebarToggle(false);
    }
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Scroll to heading
  const scrollToHeading = (headingId) => {
    const element = document.getElementById(headingId) || 
                    document.querySelector(`[data-heading-id="${headingId}"]`);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Handle download
  const handleDownload = (format, content) => {
    try {
      if (format === 'pdf') {
        const pdf = generateDocumentationPDF(content, 'Besper API Documentation');
        pdf.save('besper-documentation.pdf');
      } else if (format === 'txt') {
        const text = generateDocumentationTXT(content, 'Besper API Documentation');
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'besper-documentation.txt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error generating download:', error);
      alert('Error generating download. Please try again.');
    }
  };
  
  // Render code block
  const renderCode = (code, language = 'bash') => {
    if (renderCodeBlock) {
      return renderCodeBlock(code, language);
    }
    return <CodeBlock code={code} language={language} copyable={true} />;
  };
  
  // Render content
  const renderItemContent = () => {
    if (!currentItem) return null;
    
    if (renderContent) {
      return renderContent(currentItem);
    }
    
    const { content } = currentItem;
    if (!content) return null;
    
    return (
      <div className="docs-content-wrapper">
        {/* Overview */}
        {content.overview && (
          <section className="docs-section" data-heading-id="overview">
            <p className="docs-section-text">{content.overview}</p>
          </section>
        )}
        
        {/* Steps */}
        {content.steps && content.steps.length > 0 && (
          <div className="docs-section">
            {content.steps.map((step, index) => (
              <div key={index} className="docs-step" data-heading-id={`step-${index}`}>
                <h2 className="docs-step-title">{step.title}</h2>
                {step.description && (
                  <p className="docs-step-description">{step.description}</p>
                )}
                {step.code && renderCode(step.code, step.language || 'bash')}
              </div>
            ))}
          </div>
        )}
        
        {/* Methods */}
        {content.methods && content.methods.length > 0 && (
          <div className="docs-section">
            {content.methods.map((method, index) => (
              <div key={index} className="docs-method" data-heading-id={`method-${index}`}>
                <h2 className="docs-method-title">{method.title}</h2>
                {method.description && (
                  <p className="docs-method-description">{method.description}</p>
                )}
                {method.code && renderCode(method.code, method.language || 'bash')}
              </div>
            ))}
          </div>
        )}
        
        {/* Setup */}
        {content.setup && content.setup.length > 0 && (
          <div className="docs-section">
            {content.setup.map((item, index) => (
              <div key={index} className="docs-setup-item" data-heading-id={`setup-${index}`}>
                <h2 className="docs-setup-title">{item.title}</h2>
                {item.description && (
                  <p className="docs-setup-description">{item.description}</p>
                )}
                {item.code && renderCode(item.code, item.language || 'bash')}
              </div>
            ))}
          </div>
        )}
        
        {/* Installation */}
        {content.installation && (
          <div className="docs-section" data-heading-id="installation">
            {renderCode(content.installation, 'bash')}
          </div>
        )}
        
        {/* Usage */}
        {content.usage && (
          <div className="docs-section" data-heading-id="usage">
            {renderCode(content.usage, content.usageLanguage || 'javascript')}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className={`documentation-layout ${isDarkMode ? 'dark-mode' : ''} ${className}`}>
      {/* Mobile Sidebar Toggle */}
      {isMobile && (
        <button
          className="docs-mobile-toggle"
          onClick={() => onMobileSidebarToggle && onMobileSidebarToggle(!mobileSidebarOpen)}
          aria-label="Toggle documentation sidebar"
        >
          <Icons.Menu size={20} />
        </button>
      )}
      
      <div className="docs-layout-container">
        {/* Left Sidebar - Navigation */}
        <aside 
          ref={leftSidebarRef}
          className={`docs-sidebar-left ${isMobile && !mobileSidebarOpen ? 'hidden' : ''}`}
        >
          {/* Search */}
          {searchEnabled && (
            <div className="docs-search-wrapper">
              <div className="docs-search-input-wrapper">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="docs-search-input"
                />
              </div>
            </div>
          )}
          
          {/* Navigation */}
          <nav className="docs-nav">
            {filteredSections.map(section => {
              const isExpanded = expandedSections[section.id] ?? (section.id === selectedSection);
              const hasItems = section.items && section.items.length > 0;
              const isDisabled = section.disabled || section.upcoming;
              
              return (
                <div key={section.id} className={`docs-nav-section ${isDisabled ? 'disabled' : ''}`}>
                  <button
                    className={`docs-nav-section-header ${section.id === selectedSection ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => !isDisabled && hasItems && handleSectionToggle(section.id)}
                    disabled={isDisabled}
                  >
                    <span className="docs-nav-section-title">
                      → {section.title.toUpperCase()}
                      {section.upcoming && <span className="docs-upcoming-badge"> (UPCOMING)</span>}
                    </span>
                    {hasItems && !isDisabled && (
                      <Icons.ChevronDown 
                        size={12} 
                        className={`docs-nav-chevron ${isExpanded ? 'expanded' : ''}`}
                      />
                    )}
                  </button>
                  
                  {hasItems && isExpanded && !isDisabled && (
                    <div className="docs-nav-items">
                      {(() => {
                        // Group items by group headers
                        const groupedItems = [];
                        let currentGroup = null;
                        let currentGroupItems = [];
                        
                        section.items.forEach(item => {
                          if (item.groupHeader) {
                            // Save previous group or ungrouped items if they exist
                            if (currentGroup) {
                              groupedItems.push({
                                group: currentGroup,
                                items: currentGroupItems
                              });
                            } else if (currentGroupItems.length > 0) {
                              // Items before first group header
                              groupedItems.push({
                                group: null,
                                items: currentGroupItems
                              });
                            }
                            // Start new group
                            currentGroup = item;
                            currentGroupItems = [];
                          } else {
                            // Add item to current group (or no group)
                            currentGroupItems.push(item);
                          }
                        });
                        
                        // Don't forget the last group or ungrouped items
                        if (currentGroup) {
                          groupedItems.push({
                            group: currentGroup,
                            items: currentGroupItems
                          });
                        } else if (currentGroupItems.length > 0) {
                          // Items without any group header
                          groupedItems.push({
                            group: null,
                            items: currentGroupItems
                          });
                        }
                        
                        return groupedItems.map(({ group, items }, groupIndex) => {
                          const groupKey = group ? `${section.id}-${group.id}` : `${section.id}-ungrouped-${groupIndex}`;
                          const isGroupExpanded = expandedGroups[groupKey] ?? false; // Default to collapsed
                          
                          return (
                            <div key={groupKey} className="docs-nav-group">
                              {group && (
                                <button
                                  className="docs-nav-group-header docs-nav-group-header-clickable"
                                  onClick={() => handleGroupToggle(group.id, section.id)}
                                >
                                  <span className="docs-nav-group-header-title">{group.title}</span>
                                  <Icons.ChevronDown
                                    size={10}
                                    className={`docs-nav-group-chevron ${isGroupExpanded ? 'expanded' : ''}`}
                                  />
                                </button>
                              )}
                              {isGroupExpanded && (
                                <div className="docs-nav-group-items">
                                  {items.map(item => (
                                    <button
                                      key={item.id}
                                      className={`docs-nav-item ${selectedItem === item.id ? 'active' : ''}`}
                                      onClick={() => handleItemSelect(section.id, item.id)}
                                    >
                                      <div className="docs-nav-item-content">
                                        <div className="docs-nav-item-title">{item.title}</div>
                                        {item.description && (
                                          <div className="docs-nav-item-description">{item.description}</div>
                                        )}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>
        
        {/* Main Content */}
        <main ref={mainContentRef} className="docs-main-content">
          {currentItem ? (
            <>
              {/* Page Header */}
              <header className="docs-page-header">
                <div className="docs-page-header-top">
                  <div className="docs-page-breadcrumb">
                    <span className="docs-breadcrumb-section">
                      {sections.find(s => s.id === selectedSection)?.title.toUpperCase()}
                    </span>
                    <span className="docs-breadcrumb-separator">/</span>
                    <span className="docs-breadcrumb-item">{currentItem.title}</span>
                  </div>
                  <button
                    className="docs-download-button"
                    onClick={() => setDownloadModalOpen(true)}
                    title="Download Documentation"
                  >
                    <Icons.Download size={18} />
                    <span>Download</span>
                  </button>
                </div>
                <h1 className="docs-page-title">{currentItem.title}</h1>
                {currentItem.description && (
                  <p className="docs-page-description">{currentItem.description}</p>
                )}
                {currentItem.tags && currentItem.tags.length > 0 && (
                  <div className="docs-page-tags">
                    {currentItem.tags.map(tag => (
                      <span key={tag} className="docs-page-tag">{tag.toUpperCase()}</span>
                    ))}
                  </div>
                )}
              </header>
              
              {/* Content */}
              {renderItemContent()}
            </>
          ) : (
            <div className="docs-empty-state">
              <p>Select an item from the navigation to view documentation.</p>
            </div>
          )}
        </main>
        
        {/* Right Sidebar - "On this page" TOC */}
        {headings.length > 0 && (
          <aside ref={rightSidebarRef} className="docs-sidebar-right">
            <div className="docs-toc-wrapper">
              <h3 className="docs-toc-title">ON THIS PAGE</h3>
              <nav className="docs-toc-nav">
                {headings.map(heading => (
                  <a
                    key={heading.id}
                    href={`#${heading.id}`}
                    className={`docs-toc-link ${activeHeadingId === heading.id ? 'active' : ''}`}
                    style={{ paddingLeft: `${(heading.level - 1) * 16}px` }}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToHeading(heading.id);
                    }}
                  >
                    {heading.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        )}
      </div>
      
      {/* Download Modal */}
      <DownloadDocumentationModal
        isOpen={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        sections={sections}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default DocumentationLayout;

